import { useBottomSheet } from '../../../../../../state-management/contexts/BottomSheetContext'
import { useToast } from '../../../../../../state-management/contexts/ToastContext'
import type { SipStopRecommendation } from '../../../sip/api/sipPlan.types'
import { postFilledSipPlanCta } from '../../fundExecutionHelpers'
import {
  FolioSourceId,
  SipFolioActionTone,
  type SipFolioRow
} from '../folio'
import { SipFolioRowList, SipModifySheetLayout } from '../ModifySheet'
import { EventNames } from '../../../../../../types/misc.types'
import { trackEvent } from '../../../../../../utils/appBridge'
import { PlansExecutionEventIds, PlansScreen } from '../../../analytics/plansEvents'

const COPY = {
  actionText: 'Stop this SIP',
  externalHelper:
    'Please open the app or platform you used to buy this fund and stop this SIP from there. External SIPs can only be stopped from the platform they were started on.',
  infiniteHelper: 'Contact your circle manager for more information'
} as const

export function useStopSipAction(options?: { isRecommendationSheetSource?: boolean }) {
  const {
    openBottomSheet,
    closeBottomSheet,
    openNestedBottomSheet,
    closeNestedBottomSheet
  } = useBottomSheet()
  const openSheet = options?.isRecommendationSheetSource ? openNestedBottomSheet : openBottomSheet
  const closeSheet = options?.isRecommendationSheetSource ? closeNestedBottomSheet : closeBottomSheet

  function onStopSip(recommendation: SipStopRecommendation, manageSipCta: unknown) {
    const fundName = recommendation?.fundName ?? ''
    const fundIsin = recommendation?.isin ?? ''
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: PlansScreen.SipYourPlanRecommendation,
        eventId: PlansExecutionEventIds.SipStop,
        buttonLabel: 'Stop SIP',
        ...(fundName ? { fundName } : {}),
        ...(fundIsin ? { fundIsin } : {})
      }
    })
    openSheet(
      <StopSipModifyBottomSheet
        onDismiss={closeSheet}
        recommendation={recommendation}
        manageSipCta={manageSipCta}
      />,
      closeSheet
    )
  }

  return { onStopSip }
}

export function StopSipModifyBottomSheet({
  onDismiss,
  recommendation,
  manageSipCta
}: {
  onDismiss: () => void
  recommendation: SipStopRecommendation
  manageSipCta: unknown
}) {
  const { showToast } = useToast()

  return (
    <SipModifySheetLayout
      onDismiss={onDismiss}
      mfLogoUrl={recommendation?.mfLogoUrl ?? ''}
      fundName={recommendation?.fundName ?? ''}
      fundIsin={recommendation?.isin ?? ''}
      heading="Stop SIP in this fund"
    >
      <SipFolioRowList
        rows={buildStopFolioRows(recommendation)}
        fundName={recommendation?.fundName ?? ''}
        fundIsin={recommendation?.isin ?? ''}
        onPowerUpClick={() => {
          postFilledSipPlanCta(manageSipCta, { isin: recommendation?.isin ?? '' }, showToast)
        }}
      />
    </SipModifySheetLayout>
  )
}

/**
 * Product display order: PowerUp → External → Infinite.
 * One stop card per folio that currently has an SIP.
 * Only PowerUp is tappable (manage CTA); External / Infinite are info + helper.
 */
function buildStopFolioRows(recommendation: SipStopRecommendation): SipFolioRow[] {
  const rows: SipFolioRow[] = []
  const powerupSipAmount = recommendation?.powerupSipAmount ?? 0
  const externalSipAmount = recommendation?.externalSipAmount ?? 0
  const infiniteSipAmount = recommendation?.infiniteSipAmount ?? 0

  if (powerupSipAmount > 0) {
    rows.push({
      id: FolioSourceId.POWERUP,
      currentAmount: powerupSipAmount,
      actionText: COPY.actionText,
      actionTone: SipFolioActionTone.DANGER,
      helperText: null,
      interactive: true
    })
  }

  if (externalSipAmount > 0) {
    rows.push({
      id: FolioSourceId.EXTERNAL,
      currentAmount: externalSipAmount,
      actionText: COPY.actionText,
      actionTone: SipFolioActionTone.DANGER,
      helperText: COPY.externalHelper,
      interactive: false
    })
  }

  if (infiniteSipAmount > 0) {
    rows.push({
      id: FolioSourceId.INFINITE,
      currentAmount: infiniteSipAmount,
      actionText: COPY.actionText,
      actionTone: SipFolioActionTone.DANGER,
      helperText: COPY.infiniteHelper,
      interactive: false
    })
  }

  return rows
}
